<template>
	<Panel title="E-Contract">
		<div
			class="econtract-panel"
			v-if="eContractExists"
		>
			<!-- Unified status container for all states -->
			<div class="status-container">
				<div class="status-icon">
					<!-- Pulsing icon for validation and generation -->
					<div
						v-if="isValidating || isAutoGenerating"
						class="icon-animation"
					>
						<div class="pulse-ring"></div>
						<div
							class="icon-circle"
							:class="isValidating ? 'info' : 'info'"
						>
							<i class="fas fa-file-contract"></i>
						</div>
					</div>
					<!-- Validation successful icon -->
					<div
						v-else-if="isValidationSuccessful"
						class="icon-circle success"
					>
						<i class="fas fa-check"></i>
					</div>
					<!-- Generation in progress icon -->
					<div
						v-else-if="isGenerating"
						class="icon-circle info"
					>
						<i class="fas fa-cog fa-spin"></i>
					</div>
					<!-- Generation complete icon -->
					<div
						v-else-if="isGenerationComplete"
						class="icon-circle success"
					>
						<i class="fas fa-file-contract"></i>
					</div>
					<!-- Validation failed icon -->
					<div
						v-else-if="isValidationFailed"
						class="icon-circle error"
					>
						<i class="fas fa-exclamation"></i>
					</div>
					<!-- Static icon for other states -->
					<div
						v-else
						class="icon-circle"
						:class="statusClass"
					>
						<i :class="statusIcon"></i>
					</div>
				</div>
				<div class="status-details">
					<h3
						class="status-title"
						:class="statusClass + '-title'"
					>
						{{ statusText }}
					</h3>

					<!-- Status-specific messaging -->
					<div
						v-if="isValidating"
						class="status-message"
					>
						<p>
							<strong>{{ lenderName }}</strong> is currently validating your deal and eContract.
						</p>
					</div>
					<div
						v-else-if="isAutoGenerating"
						class="status-message"
					>
						<p>
							Validation was successful! We're now generating your eContract with <strong>{{ lenderName }}</strong
							>.
						</p>
					</div>
					<div
						v-else-if="isValidationSuccessful"
						class="status-message"
					>
						<p>Contract validation has been completed successfully.</p>
					</div>
					<div
						v-else-if="isGenerating"
						class="status-message"
					>
						<p>Your contract is currently being generated.</p>
					</div>
					<div
						v-else-if="isGenerationComplete"
						class="status-message"
					>
						<p>Your eContract has been successfully generated.</p>
					</div>
					<div
						v-else-if="isValidationFailed"
						class="status-message"
					>
						<p>Contract validation could not be completed due to issues.</p>
					</div>

					<p
						class="status-date"
						v-if="eContract.contractDate"
					>
						Contract Date: {{ formatDate(eContract.contractDate) }}
					</p>
					<p
						class="provider-info"
						v-if="eContract.provider && $global.isAdminView"
					>
						Provider DJ ID: {{ selectedApplication?.dealJacketId }}
					</p>
					<p
						class="provider-info"
						v-if="eContract.providerContractId && $global.isAdminView"
					>
						Provider eC ID: {{ eContract.providerContractId }}
					</p>
					<p
						class="provider-info"
						v-if="eContract.conversationId && $global.isAdminView"
					>
						Conversation ID: {{ eContract.conversationId }}
					</p>
					<p
						class="provider-info"
						v-if="eContract.provider && $global.isAdminView"
					>
						{{ FinanceChannelsProvider[eContract.provider] }}
					</p>
				</div>
			</div>

			<!-- Messages section -->
			<div
				class="messages-container"
				v-if="hasErrors || hasMessages"
			>
				<div
					v-if="hasErrors"
					class="message-section error-section"
				>
					<h4>Issues:</h4>
					<div class="message-list">
						<div
							v-for="(message, index) in eContract.errorMessages"
							:key="'error-' + index"
							class="message-item error-item"
						>
							<div class="message-indicator">
								<i class="fas fa-times-circle"></i>
							</div>
							<div class="message-content">{{ message }}</div>
						</div>
					</div>
				</div>

				<div
					v-if="hasMessages"
					class="message-section info-section"
				>
					<h4>Information:</h4>
					<div class="message-list">
						<div
							v-for="(message, index) in eContract.messages"
							:key="'info-' + index"
							class="message-item info-item"
						>
							<div class="message-indicator">
								<i class="fas fa-info-circle"></i>
							</div>
							<div class="message-content">{{ message }}</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Actions -->
			<div class="econtract-actions">
				<!-- <button @click="showEContractStatus" class="button button-primary ignore-all-locks">
                    <i class="fas fa-eye"></i> View Details
                </button> -->

				<button
					@click="handleEContract"
					:disabled="isEContracting"
					class="button button-primary ignore-deal-lock"
					v-show="$global.isAdminView"
				>
					<i
						v-if="isEContracting"
						class="fas fa-spinner fa-spin"
					/>
					<i
						v-else
						class="fas fa-file-contract"
					/>
					E-Contract
				</button>
			</div>
		</div>

		<div
			class="no-econtract"
			v-else
		>
			<div class="empty-state">
				<i class="fas fa-file-contract"></i>
				<p>No E-Contract available for this deal</p>
			</div>
		</div>
	</Panel>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
	import FIMenu from '@core/classes/FIMenu';
	import Panel from '@core/components/Panel.vue';
	import EContract, { EContractStatus } from '@core/classes/EContract';
	import $modal from '@core/services/modal';
	import ModalEContractStatus from '@core/modals/modalEContractStatus.vue';
	import { CreateEContract, HandleEContractStatus } from '@core/helpers/finance-channels-helper';
	import { FinanceChannelsProvider, PartnerCodeType } from '@core/classes/SharedEnums';
	import api from '@core/services/api/_api';
	import settings from 'settings';
	import StoreClass from '@core/classes/Store/Store';
	import util from '@core/services/util';

	const props = defineProps<{
		fimenu: FIMenu;
	}>();

	const eContract = computed(() => props.fimenu.dealJacket.eContract);
	const eContractExists = computed(() => eContract.value !== null);
	const isEContracting = ref(false);

	// Status information
	const statusText = computed(() => EContract.getStatusText(eContract.value?.status));
	const statusClass = computed(() => EContract.getStatusClass(eContract.value?.status));
	const statusIcon = computed(() => EContract.getStatusIcon(eContract.value?.status));

	// Status flags
	const isAutoGenerating = ref(false);
	const isValidating = computed(
		() => eContract.value?.status === EContractStatus.ValidationInProgress || eContract.value?.status === EContractStatus.ValidationAcknowledged,
	);
	const isValidationSuccessful = computed(() => eContract.value?.status === EContractStatus.ValidationSuccessful && !isAutoGenerating.value);
	const isValidationFailed = computed(() => eContract.value?.status === EContractStatus.ValidationFailed);
	const isGenerating = computed(() => eContract.value?.status === EContractStatus.GenerationInProgress || isAutoGenerating.value);
	const isGenerationComplete = computed(() => eContract.value?.status === EContractStatus.GenerationComplete);

	// Messages
	const hasErrors = computed(() => eContract.value?.errorMessages && eContract.value.errorMessages.length > 0);
	const hasMessages = computed(() => eContract.value?.messages && eContract.value.messages.length > 0);

	// Provider info
	const selectedApplication = computed(() => props.fimenu.dealJacket.getSelectedApplication());

	// Format date for display
	const formatDate = (date: string | null) => {
		if (!date) return '';
		return util.toMoment(date).format('MM/DD/YYYY');
	};

	// Lender name
	const lenderName = computed(() => props.fimenu.lender.lenderName);

	// Show the full eContract status modal
	const showEContractStatus = () => {
		$modal.open(ModalEContractStatus, {
			name: 'modalEContractStatus',
			passedData: {
				fimenu: props.fimenu,
			},
			backdrop: false,
		});
	};

	const handleEContract = async () => {
		try {
			if (props.fimenu.isCash()) return;

			isEContracting.value = true;
			await CreateEContract(props.fimenu);
		} catch (err) {
			console.error(err);
		} finally {
			isEContracting.value = false;
		}
	};

	// Polling implementation
	const statusToPoll = [EContractStatus.ValidationInProgress, EContractStatus.GenerationInProgress, EContractStatus.ValidationAcknowledged];
	const pollInterval = ref<NodeJS.Timeout | null>(null);

	const startPolling = () => {
		// Clear any existing interval first to avoid multiple intervals
		stopPolling();

		pollInterval.value = setInterval(async () => {
			const status = await fetchEContractStatus();
			if (status && props.fimenu.dealJacket.eContract) {
				props.fimenu.dealJacket.eContract.status = status;

				if (status == EContractStatus.ValidationSuccessful) {
					isAutoGenerating.value = true;
					try {
						// Handle eContract status but disable modal showing
						const result = await HandleEContractStatus(props.fimenu, false);
						// Update our local status from the result
						if (result.status && props.fimenu.dealJacket.eContract) {
							props.fimenu.dealJacket.eContract.status = result.status;
						}
					} finally {
						isAutoGenerating.value = false;
					}
				}

				if (!statusToPoll.includes(status)) {
					stopPolling();
				}
			}
		}, 30000); // Poll every 30 seconds
	};

	const stopPolling = () => {
		if (pollInterval.value) {
			util.log('Stopping eContract status polling');
			clearInterval(pollInterval.value);
			pollInterval.value = null;
		}
	};

	const fetchEContractStatus = async (): Promise<EContractStatus> => {
		try {
			const dealerId = props.fimenu.dealJacket.providersInfo.find(pi => pi.providerDealJacketId === selectedApplication.value?.dealJacketId)?.providerStoreCode;
			const providerDealJacketId = selectedApplication.value?.dealJacketId;
			const store: StoreClass | null = settings?.userStores?.find((s: StoreClass) => s.storeCode == props.fimenu.storeCode);
			const partnerId = store?.partnerCodes.find(pc => pc.type == PartnerCodeType.RouteOnePartnerId)?.code;

			if (!providerDealJacketId || !dealerId || !partnerId) {
				const missing = [];
				if (!providerDealJacketId) missing.push('providerDealJacketId');
				if (!dealerId) missing.push('dealerId');
				if (!partnerId) missing.push('partnerId');
				console.error(`Missing required parameters for eContractStatus request: ${missing.join(', ')}`);
				return null;
			}

			const response = await api.financeChannels.getEContractStatus(providerDealJacketId, dealerId, partnerId);
			return response?.data?.payload;
		} catch (err) {
			console.error(err);
			return null;
		}
	};

	// Watch for eContract status changes to start/stop polling as needed
	watch(
		() => eContract.value?.status,
		(newStatus, oldStatus) => {
			if (newStatus && statusToPoll.includes(newStatus)) {
				util.log(`EContract status changed to ${newStatus}, starting polling`);
				startPolling();
			} else if (newStatus && !statusToPoll.includes(newStatus) && pollInterval.value) {
				util.log(`EContract status changed to ${newStatus}, stopping polling`);
				stopPolling();
			}
		},
		{ immediate: false },
	);

	onMounted(() => {
		if (eContractExists.value && eContract.value && statusToPoll.includes(eContract.value.status)) {
			util.log(`Initializing with eContract status ${eContract.value.status}, starting polling`);
			startPolling();
		}
	});

	onUnmounted(() => {
		stopPolling();
	});

	function useRef(arg0: boolean) {
		throw new Error('Function not implemented.');
	}
</script>

<style scoped>
	.econtract-panel {
		padding: 1rem;
	}

	.status-container {
		display: flex;
		margin-bottom: 1rem;
		align-items: flex-start;
	}

	.status-icon {
		margin-right: 1.5rem;
		flex-shrink: 0;
		position: relative;
	}

	/* Animation container */
	.icon-animation {
		position: relative;
		width: 3.5rem;
		height: 3.5rem;
	}

	/* Pulse animation */
	.pulse-ring {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 100%;
		height: 100%;
		border-radius: 50%;
		animation: pulse 2s ease-out infinite;
		border: 2px solid rgba(33, 150, 243, 0.3);
		z-index: 1;
	}

	@keyframes pulse {
		0% {
			transform: translate(-50%, -50%) scale(0.9);
			opacity: 0.8;
		}
		50% {
			opacity: 0.4;
		}
		100% {
			transform: translate(-50%, -50%) scale(1.3);
			opacity: 0;
		}
	}

	.icon-circle {
		width: 3.5rem;
		height: 3.5rem;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		position: relative;
		z-index: 2;
	}

	.icon-animation .icon-circle {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.icon-circle.success {
		background-color: #4caf50;
	}

	.icon-circle.error {
		background-color: #f44336;
	}

	.icon-circle.warning {
		background-color: #ff9800;
	}

	.icon-circle.info {
		background-color: #2196f3;
	}

	.icon-circle.final {
		background-color: #9c27b0;
	}

	.icon-circle.unknown {
		background-color: #9e9e9e;
	}

	.status-details {
		flex: 1;
	}

	.status-title {
		margin: 0 0 0.5rem 0;
		font-size: 1.2rem;
		font-weight: 600;
	}

	.status-title.success-title {
		color: #4caf50;
	}

	.status-title.error-title {
		color: #f44336;
	}

	.status-title.info-title {
		color: #2196f3;
	}

	.status-title.warning-title {
		color: #ff9800;
	}

	.status-message {
		margin-bottom: 0.75rem;
	}

	.status-message p {
		margin: 0;
		font-size: 0.9rem;
		line-height: 1.4;
	}

	.status-date,
	.provider-info {
		margin: 0.35rem 0;
		font-size: 0.9rem;
		color: #666;
	}

	.messages-container {
		border-top: 1px solid #eee;
		padding-top: 1rem;
		margin-top: 1rem;
	}

	.message-section {
		margin-bottom: 1rem;
	}

	.message-section h4 {
		margin: 0 0 0.5rem 0;
		font-size: 1rem;
	}

	.message-list {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.message-item {
		display: flex;
		align-items: flex-start;
		padding: 0.5rem;
		border-radius: 4px;
	}

	.error-item {
		background-color: rgba(244, 67, 54, 0.1);
	}

	.info-item {
		background-color: rgba(33, 150, 243, 0.1);
	}

	.message-indicator {
		margin-right: 0.5rem;
		color: #f44336;
	}

	.info-item .message-indicator {
		color: #2196f3;
	}

	.message-content {
		flex: 1;
		font-size: 0.9rem;
	}

	.econtract-actions {
		display: flex;
		justify-content: flex-end;
		margin-top: 1rem;
		padding-top: 1rem;
		border-top: 1px solid #eee;
		gap: 10px;
	}

	.econtract-actions button {
		max-width: 150px;
	}

	.no-econtract {
		padding: 2rem;
		text-align: center;
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		background-color: #f9f9f9;
		border-radius: 8px;
	}

	.empty-state i {
		font-size: 3rem;
		color: #ccc;
		margin-bottom: 1rem;
	}

	.empty-state p {
		margin: 0;
		color: #666;
	}
</style>
